import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Skeleton,
  StatusPill,
  Textarea
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import {
  IconAlert,
  IconArrowLeft,
  IconBan,
  IconCases,
  IconCheck,
  IconCheckCircle,
  IconClock,
  IconCourier,
  IconDownload,
  IconMail,
  IconNote,
  IconPackage,
  IconRefresh,
  IconUser
} from '../../components/icons';
import {
  actionLabels,
  actionTone,
  billedAsLabels,
  billedAsTone,
  caseStatusLabels,
  caseStatusTone,
  formatDateTime,
  inferBilledAs,
  issueTypeLabels,
  raisedByLabel,
  raisedByTone
} from '../../components/admin/labels';
import { UpgradeModal } from '../../components/merchant/PlanUpgradeGrid';
import { merchantRoleLabel } from '../../components/plans';

/**
 * The four decisions, written out at card scale the way the magic-link case
 * page shows them: green resolves, blue replaces, amber is still open and
 * waiting on the courier, red leaves the customer without resolution.
 *
 * Tailwind only emits classes it can see literally, so every one is spelled
 * out here rather than built from a tone token.
 */
const decisions = [
  {
    value: 'refund',
    label: 'Refund customer',
    icon: IconCheck,
    card: 'border-success-100 bg-success-50/40 hover:border-success-600 hover:bg-success-50 focus-visible:ring-success-600',
    badge: 'bg-success-600 text-white',
    text: 'text-success-700'
  },
  {
    value: 'reship',
    label: 'Reship order',
    icon: IconRefresh,
    card: 'border-info-100 bg-info-50/40 hover:border-info-600 hover:bg-info-50 focus-visible:ring-info-600',
    badge: 'bg-info-600 text-white',
    text: 'text-info-700'
  },
  {
    value: 'escalate',
    label: 'Escalate to courier',
    icon: IconCourier,
    card: 'border-warning-100 bg-warning-50/40 hover:border-warning-600 hover:bg-warning-50 focus-visible:ring-warning-600',
    badge: 'bg-warning-600 text-white',
    text: 'text-warning-700'
  },
  {
    value: 'reject',
    label: 'Reject claim',
    icon: IconBan,
    card: 'border-danger-100 bg-danger-50/40 hover:border-danger-600 hover:bg-danger-50 focus-visible:ring-danger-600',
    badge: 'bg-danger-600 text-white',
    text: 'text-danger-700'
  }
];

/** Only these issue types ever reach Ship24 — see shouldCallShip24(). */
const SHIP24_ISSUE_TYPES = ['not_arrived', 'delivered_not_received'];

const SummaryItem = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ink-100 text-ink-600">
      <Icon size={17} />
    </span>
    <div className="min-w-0">
      <dt className="text-xs font-600 uppercase tracking-wide text-ink-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm font-500 text-ink-900">{children || '—'}</dd>
    </div>
  </div>
);

const MerchantCaseDetail = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const [caseData, setCaseData] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(null);
  // True for exactly one render after returning from Stripe Checkout. The
  // webhook that applies the payment can lag by seconds — or never arrive in a
  // dev environment with no `stripe listen` running — so the page would
  // otherwise re-render with the same block the merchant just paid to clear.
  const [confirmingPayment, setConfirmingPayment] = useState(
    () => searchParams.get('payment') === 'success'
  );

  const fetchCase = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/portal/cases/${caseId}`);
      setCaseData(data);
      setError(null);
    } catch (err) {
      setError(
        err.response?.status === 404
          ? 'This case does not exist, or it belongs to another merchant.'
          : 'Could not load this case.'
      );
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  const fetchMerchant = useCallback(
    () =>
      api
        .get('/api/portal/me')
        .then(({ data }) => setMerchant(data.merchant))
        .catch(() => {}),
    []
  );

  useEffect(() => {
    fetchCase();
    fetchMerchant();
  }, [fetchCase, fetchMerchant]);

  // Returning from Stripe Checkout — see confirmingPayment above.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment !== 'success' && payment !== 'cancelled') return;

    const sessionId = params.get('session_id');
    params.delete('payment');
    params.delete('session_id');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));

    if (payment === 'cancelled') {
      toast('Payment was cancelled.', { tone: 'info' });
      return;
    }
    if (!sessionId) {
      setConfirmingPayment(false);
      return;
    }

    api
      .post('/api/portal/confirm-checkout', { sessionId })
      .then(({ data }) => {
        toast(data?.activated ? 'Payment confirmed.' : 'Payment received.', { tone: 'success' });
      })
      .catch((err) => {
        console.error('Confirm checkout error:', err);
        toast('Payment received, but we could not confirm it just now — refresh in a moment.', {
          tone: 'error'
        });
      })
      .finally(() => {
        setConfirmingPayment(false);
        fetchCase();
        fetchMerchant();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitDecision = async (action, reason) => {
    setSubmitting(true);
    try {
      await api.post(`/api/portal/cases/${caseId}/action`, {
        action,
        ...(reason && { rejectionReason: reason })
      });
      toast('Decision recorded. The customer has been notified.', { tone: 'success' });
      setRejecting(false);
      setRejectionReason('');
      fetchCase();
    } catch (err) {
      toast(err.response?.data?.error || 'Could not record this decision.', { tone: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const chooseDecision = async (action) => {
    if (action === 'reject') {
      setRejecting(true);
      return;
    }
    const label = decisions.find((d) => d.value === action)?.label;
    const ok = await confirm({
      title: label,
      message: `Confirm "${label}" for case ${caseId}?`,
      detail: 'The customer is notified automatically and this cannot be undone.',
      confirmLabel: 'Confirm decision'
    });
    if (ok) await submitDecision(action);
  };

  /**
   * The evidence pack comes back as a PDF body, so this goes through axios with
   * responseType blob rather than the usual JSON path. An error response is a
   * blob too — it has to be read back as text before its message can be shown.
   */
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/api/portal/cases/${caseId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `evidence-pack-${caseId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      // Downloading is a billable action the first time, so the case's billing
      // state on screen is stale the moment the file lands.
      fetchCase();
    } catch (err) {
      let message = 'Could not download the evidence pack.';
      try {
        const text = await err.response?.data?.text?.();
        const parsed = text ? JSON.parse(text) : null;
        if (parsed?.error) message = parsed.error;
      } catch {
        /* not a JSON error body — keep the generic message */
      }
      toast(message, { tone: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const openUpgrade = (context = null) => {
    setUpgradeContext(context);
    setShowUpgrade(true);
  };

  if (confirmingPayment) {
    return (
      <Card>
        <CardBody>
          <p className="py-10 text-center text-sm text-ink-500">Confirming your payment…</p>
        </CardBody>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <Card>
        <EmptyState
          icon={IconCases}
          title="Case not available"
          description={error || 'This case could not be loaded.'}
          action={
            <Button variant="secondary" size="sm" onClick={() => navigate('/merchant/cases')}>
              <IconArrowLeft size={15} />
              Back to cases
            </Button>
          }
        />
      </Card>
    );
  }

  const delivery = caseData.deliveryDetails || {};
  const files = caseData.evidence?.files || [];
  const events = caseData.ship24?.events || [];
  const blocked = caseData.actionBlocked;
  const decided = Boolean(caseData.merchant?.action);
  const billedAs = inferBilledAs(caseData);
  const tracksWithShip24 = SHIP24_ISSUE_TYPES.includes(caseData.issueType);

  /**
   * The server applies one gate to both acting and downloading
   * (caseAccessService), differing only in wording — so a case blocked for
   * action is blocked for the evidence pack too, and the download button is
   * hidden rather than offered and then refused with a 403.
   */
  const gated = Boolean(blocked);

  return (
    <div className="space-y-6">
      {/* Back + status bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate('/merchant/cases')}
          className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-ink-500 transition-colors hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
        >
          <IconArrowLeft size={15} />
          Back to cases
        </button>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={caseStatusTone[caseData.status] || 'neutral'}>
            {caseStatusLabels[caseData.status] || caseData.status}
          </StatusPill>
          <StatusPill tone={raisedByTone(caseData)}>{raisedByLabel(caseData)}</StatusPill>
          <Button variant="ghost" size="sm" onClick={fetchCase}>
            <IconRefresh size={15} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary */}
      <Card>
        <CardHeader
          title={issueTypeLabels[caseData.issueType] || caseData.issueType}
          description={`Case ${caseData.caseId}`}
          actions={
            !gated && (
              <Button size="sm" onClick={downloadPdf} loading={downloading}>
                <IconDownload size={15} />
                Evidence pack
              </Button>
            )
          }
        />
        <CardBody>
          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <SummaryItem icon={IconUser} label="Customer">
              {caseData.customer?.name}
            </SummaryItem>
            <SummaryItem icon={IconMail} label="Email">
              {caseData.customer?.email}
            </SummaryItem>
            <SummaryItem icon={IconClock} label="Submitted">
              {formatDateTime(caseData.submittedAt)}
            </SummaryItem>
            <SummaryItem icon={IconCourier} label={delivery.courier || 'Tracking'}>
              {delivery.trackingNumber}
            </SummaryItem>
            <SummaryItem icon={IconNote} label="Order">
              {delivery.orderNumber ? `#${delivery.orderNumber}` : null}
            </SummaryItem>
            <SummaryItem icon={IconPackage} label="Postcode">
              {delivery.postcode}
            </SummaryItem>
            <SummaryItem icon={IconUser} label="Raised by">
              {caseData.createdBy === 'merchant' ? (
                <>
                  {caseData.createdByUser?.name || 'Your team'}
                  {caseData.createdByUser?.merchantRole && (
                    <span className="ml-1.5 text-xs font-400 text-ink-500">
                      ({merchantRoleLabel(caseData.createdByUser.merchantRole)})
                    </span>
                  )}
                </>
              ) : (
                <>
                  Customer
                  <span className="ml-1.5 text-xs font-400 text-ink-500">
                    (reported it themselves)
                  </span>
                </>
              )}
            </SummaryItem>
            <SummaryItem icon={IconCases} label="Billed as">
              {billedAs ? (
                <StatusPill tone={billedAsTone[billedAs] || 'neutral'}>
                  {billedAsLabels[billedAs] || billedAs}
                </StatusPill>
              ) : caseData.billingCountedAt ? (
                'Billed (plan unknown)'
              ) : (
                'Not yet'
              )}
            </SummaryItem>
            {caseData.dor?.signed && (
              <SummaryItem icon={IconCheckCircle} label="Declaration">
                Signed {formatDateTime(caseData.dor.timestamp)}
              </SummaryItem>
            )}
          </dl>
        </CardBody>
      </Card>

      {/* What the customer reported */}
      {caseData.evidence?.comments && (
        <Card>
          <CardHeader title="What the customer reported" />
          <CardBody>
            <blockquote className="rounded-r-lg border-l-[3px] border-ink-300 bg-ink-50 px-4 py-3 text-sm italic leading-relaxed text-ink-700">
              &ldquo;{caseData.evidence.comments}&rdquo;
            </blockquote>
          </CardBody>
        </Card>
      )}

      {/* Evidence */}
      <Card>
        <CardHeader
          title="Evidence"
          description={
            files.length
              ? `${files.length} file${files.length === 1 ? '' : 's'} uploaded by the customer`
              : 'Nothing was uploaded with this case'
          }
        />
        <CardBody>
          {files.length ? (
            <div className="space-y-3">
              {files.map((file, i) => {
                const isImage = file.mimetype?.startsWith('image/');
                return (
                  <div key={i} className="overflow-hidden rounded-xl border border-ink-200">
                    <div className="flex items-center gap-2.5 border-b border-ink-200 bg-white px-3.5 py-2.5">
                      <IconNote size={16} className="shrink-0 text-ink-500" />
                      <span className="min-w-0 flex-1 truncate text-sm font-500 text-ink-900">
                        {file.filename}
                      </span>
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 whitespace-nowrap text-sm font-600 text-brand-700 hover:underline"
                      >
                        {isImage ? 'Open full size' : 'Open file'}
                      </a>
                    </div>
                    {isImage && file.url && (
                      <div className="flex min-h-[120px] justify-center bg-ink-100 p-2">
                        <img
                          src={file.url}
                          alt={file.filename}
                          className="h-auto max-h-[280px] w-auto max-w-full rounded object-contain"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-ink-500">No files were uploaded.</p>
          )}
        </CardBody>
      </Card>

      {/* Courier tracking. shouldCallShip24() never fetches for `damaged` or
          `missing_items`, and `not_arrived` sits behind a delay timer — so an
          empty list is expected rather than a fault for the two types that do
          fetch, and the card is omitted entirely for the ones that never do. */}
      {(events.length > 0 || tracksWithShip24) && (
        <Card>
          <CardHeader title="Courier tracking" description="From Ship24." />
          <CardBody>
            {events.length ? (
              <ol className="space-y-3">
                {events.map((event, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
                      <IconCourier size={13} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-500 text-ink-900">
                        {event.status}
                        {event.location ? ` — ${event.location}` : ''}
                      </p>
                      {event.description && event.description !== event.status && (
                        <p className="mt-0.5 text-sm text-ink-600">{event.description}</p>
                      )}
                      <p className="mt-0.5 text-xs text-ink-500">
                        {formatDateTime(event.timestamp)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-ink-500">
                No courier tracking events yet for this tracking number.
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Decision */}
      {decided ? (
        <Card>
          <CardHeader title="Your decision" />
          <CardBody>
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill tone={actionTone[caseData.merchant.action] || 'neutral'}>
                {actionLabels[caseData.merchant.action] || caseData.merchant.action}
              </StatusPill>
              <span className="text-sm text-ink-500">
                {formatDateTime(caseData.merchant.actionTakenAt)}
              </span>
            </div>
            {caseData.merchant.rejectionReason && (
              <p className="mt-3 text-sm text-ink-700">
                <span className="font-600">Reason:</span> {caseData.merchant.rejectionReason}
              </p>
            )}
            <p className="mt-3 text-sm text-ink-500">
              The customer was notified automatically when this was recorded.
            </p>
          </CardBody>
        </Card>
      ) : gated ? (
        <Card>
          <CardHeader
            title="Action needed before you can decide"
            description="This also applies to the evidence pack download."
          />
          <CardBody>
            <div className="flex items-start gap-3 rounded-xl bg-warning-50 px-4 py-3.5 ring-1 ring-inset ring-warning-100">
              <IconAlert size={18} className="mt-0.5 shrink-0 text-warning-600" />
              <div className="min-w-0">
                <p className="text-sm text-warning-700">{blocked.error}</p>
                {/* AWAITING_REVIEW (pay-per-case) and QUOTA_FILLED (subscription
                    allowance spent) both open the plan chooser with a "continue
                    as you are" option ahead of it. LIMITED needs an invoice
                    paid, not a plan change, and the portal has no
                    invoice-payment flow yet. */}
                {blocked.code !== 'LIMITED' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      if (blocked.code === 'AWAITING_REVIEW') {
                        openUpgrade({
                          caseId: caseData.caseId,
                          kind: 'payPerCase',
                          amount: caseData.nextCycleAmount
                        });
                      } else if (blocked.code === 'QUOTA_FILLED') {
                        openUpgrade({
                          caseId: caseData.caseId,
                          kind: 'quotaFilled',
                          amount: caseData.overageAmount
                        });
                      } else {
                        openUpgrade();
                      }
                    }}
                  >
                    {blocked.code === 'QUOTA_FILLED'
                      ? 'Pay or upgrade plan'
                      : blocked.code === 'AWAITING_REVIEW'
                        ? 'Pay or change plan'
                        : 'Choose a plan'}
                  </Button>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardHeader
            title="Your decision"
            description="The customer is notified automatically when you decide."
          />
          <CardBody>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {decisions.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  disabled={submitting}
                  onClick={() => chooseDecision(d.value)}
                  className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50 ${d.card}`}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${d.badge}`}
                  >
                    <d.icon size={19} />
                  </span>
                  <span className={`font-display text-sm font-700 ${d.text}`}>{d.label}</span>
                </button>
              ))}
            </div>

            {rejecting && (
              <div className="mt-5 border-t border-ink-100 pt-5">
                <Field label="Rejection reason" htmlFor="reject-reason" required>
                  <Textarea
                    id="reject-reason"
                    rows={4}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Explain why this claim is being rejected — the customer sees this."
                    autoFocus
                  />
                </Field>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    variant="danger"
                    loading={submitting}
                    disabled={!rejectionReason.trim()}
                    onClick={() => submitDecision('reject', rejectionReason.trim())}
                  >
                    Confirm rejection
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setRejecting(false);
                      setRejectionReason('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Evidence pack */}
      <Card>
        <CardHeader
          title="Evidence pack"
          description="Everything the customer submitted, bundled as a single PDF."
        />
        <CardBody>
          {gated ? (
            <p className="text-sm text-ink-600">{blocked.error}</p>
          ) : (
            <Button onClick={downloadPdf} loading={downloading}>
              <IconDownload size={17} />
              Download evidence pack PDF
            </Button>
          )}
        </CardBody>
      </Card>

      <UpgradeModal
        open={showUpgrade}
        onClose={() => {
          setShowUpgrade(false);
          setUpgradeContext(null);
        }}
        merchant={merchant}
        caseId={upgradeContext?.caseId}
        continueKind={upgradeContext?.kind}
        continueAmount={upgradeContext?.amount}
      />
    </div>
  );
};

export default MerchantCaseDetail;
